import {
  IMarketingAlignedPoint,
  marketingMetricConfidence,
  marketingPeriodMatrixPreset,
  MarketingDeltaConfidence,
  MarketingPeriodMatrixPreset,
  MarketingStep,
  MARKETING_PERIOD_MATRIX_PRESETS,
  MARKETING_STEP_GRANULARITY,
} from './marketingCompareModel';
import {
  IMarketingRange,
  isMarketingChannel,
  marketingChannelColor,
  marketingChannelFromKey,
  marketingDeltaPoints,
  marketingDerivedMetrics,
  marketingIsShareFormat,
  marketingMetricValue,
  marketingMetricView,
  marketingNumber,
  marketingRangeDays,
  MarketingLeadMetric,
  MarketingMetricKey,
  MarketingSplit,
  MARKETING_CHANNELS,
  MARKETING_METRICS,
  MARKETING_NONE_KEY,
} from './marketingModel';
import {
  marketingFamilyColor,
  MARKETING_SPLIT_PALETTE,
  MARKETING_SPLIT_REST_COLOR,
} from './marketingPalette';
import {
  IMarketingBreakdownRow,
  IMarketingChannelShare,
  IMarketingContractsCounts,
  IMarketingFilter,
  IMarketingLeadDynamics,
  IMarketingLeadDynamicsPoint,
  IMarketingLeadGroupValue,
  IMarketingMetrics,
  IMarketingSeriesPoint,
  MarketingChannelCode,
  MarketingDate,
  MarketingGranularityCode,
} from './types';

/**
 * ТЗ 0103 — «Лиды и продажи» (design-erp §3.2): значения главного графика,
 * «Откуда лиды» с дельтой к сравнению и таблица «неделя к неделе / месяц к
 * месяцу». Чистый модуль: подписи — ключами.
 */

/**
 * Значение показателя для показа: расход — полный или за собранные дни
 * (как в плитке), прочее — с производными (конверсия лид → продажа).
 */
export const marketingShownMetric = (
  metrics: IMarketingMetrics | null | undefined,
  key: MarketingMetricKey,
): number | null =>
  metrics
    ? marketingMetricView(marketingDerivedMetrics(metrics), key).value
    : null;

/**
 * Сравнение главного графика по умолчанию — линией у любой метрики
 * (повторное ревью 0103, пакет 2, P3): сдвоенные столбики на 30 днях
 * превращались в «забор», где текущий период не отличить от прошлого.
 * «Столбцами» — переключателем над графиком.
 */
export const marketingLeadCompareStyle = (
  // Метрика остаётся в подписи: правило может снова зависеть от неё.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  metric: MarketingLeadMetric,
): 'bars' | 'line' => 'line';

/* ── «Откуда лиды» ───────────────────────────────────────────────────── */

export interface IMarketingLeadSource {
  channel: MarketingChannelCode;
  leads: number;
  /** Лиды канала в окне сравнения; `null` — сравнения нет. */
  previous: number | null;
  /** Текущее минус прошлое; `null` — сравнения нет. */
  diff: number | null;
  confidence: MarketingDeltaConfidence;
}

/**
 * CRM-лиды по каналам с дельтой к окну сравнения. Канал без лидов в обоих
 * окнах не показывается; канал, который был только в сравнении, — с нулём
 * сейчас (пропал — это новость). По убыванию лидов, при равенстве — порядок
 * словаря каналов.
 */
export const marketingLeadSources = (
  channels: ReadonlyArray<IMarketingChannelShare>,
  compare: ReadonlyArray<IMarketingChannelShare> | null | undefined,
): IMarketingLeadSource[] => {
  const before = new Map(
    (compare ?? []).map((item) => [item.channel, item.metrics.crmLeads]),
  );
  const codes = Array.from(
    new Set([
      ...channels.map((item) => item.channel),
      ...(compare ?? []).map((item) => item.channel),
    ]),
  );
  const now = new Map(channels.map((item) => [item.channel, item.metrics]));
  const order = (code: string) => {
    const index = (MARKETING_CHANNELS as string[]).indexOf(code);
    return index < 0 ? MARKETING_CHANNELS.length : index;
  };
  return codes
    .map((channel) => {
      const leads = now.get(channel)?.crmLeads ?? 0;
      const previous = compare ? before.get(channel) ?? 0 : null;
      return {
        channel,
        leads,
        previous,
        diff: previous === null ? null : leads - previous,
        confidence: marketingMetricConfidence('crmLeads', leads, previous),
      };
    })
    .filter((row) => row.leads > 0 || (row.previous ?? 0) > 0)
    .sort((a, b) => b.leads - a.leads || order(a.channel) - order(b.channel));
};

/* ── «Неделя к неделе / месяц к месяцу» ──────────────────────────────── */

/** Строки таблицы периодов — в порядке воронки и денег. */
export const MARKETING_PERIOD_MATRIX_ROWS: Array<
  MarketingMetricKey | 'crmLeadsRepeat'
> = [
  'crmLeads',
  'crmLeadsRepeat',
  'contractsSent',
  'contractsSigned',
  'sales',
  'leadToSale',
  'spendPln',
  'cplPln',
  'costPerSalePln',
  'revenuePln',
  'cashPln',
];

export type MarketingPeriodMatrixRow = typeof MARKETING_PERIOD_MATRIX_ROWS[number];

export interface IMarketingPeriodCell {
  value: number | null;
  /** Показано за собранные дни (расход, CPL…) — серым. */
  partial: boolean;
  /** Изменение к ПРЕДЫДУЩЕЙ колонке, %; `null` — не считается. */
  deltaPct: number | null;
  /**
   * Повторное ревью 0103 (A12): у долей (конверсия) — разница в п. п. к
   * предыдущей колонке вместо процента; у прочих — `null`.
   */
  deltaPoints: number | null;
  diff: number | null;
  confidence: MarketingDeltaConfidence;
  /** Значение той же корзины в окне сравнения (год назад). */
  previous: number | null;
}

const rowValue = (
  metrics: IMarketingMetrics | null,
  row: MarketingPeriodMatrixRow,
): { value: number | null; partial: boolean } => {
  if (!metrics) return { value: null, partial: false };
  if (row === 'crmLeadsRepeat') {
    return { value: metrics.crmLeadsRepeat ?? null, partial: false };
  }
  const view = marketingMetricView(marketingDerivedMetrics(metrics), row);
  return { value: view.value, partial: view.partial };
};

const confidenceOf = (
  row: MarketingPeriodMatrixRow,
  current: number | null,
  previous: number | null,
): MarketingDeltaConfidence =>
  row === 'crmLeadsRepeat'
    ? marketingMetricConfidence('crmLeads', current, previous)
    : marketingMetricConfidence(row, current, previous);

/**
 * Финальная проверка 0103 (F-E15): показатели, которые считаются от
 * CRM-лидов. До начала истории CRM (`leadHistoryFrom`) у них не «0», а «нет
 * данных»: сравнение с таким окном — «было 0» и зелёная стрелка — врёт.
 */
export const MARKETING_LEAD_BASED_METRICS: ReadonlyArray<string> = [
  'crmLeads',
  'crmLeadsRepeat',
  'leadToSale',
  'leadToContract',
  'cplPln',
];

export const marketingIsLeadBasedMetric = (key: string): boolean =>
  MARKETING_LEAD_BASED_METRICS.includes(key);

/**
 * Есть ли у корзины история CRM-лидов: `full` — корзина целиком не раньше
 * начала истории (или начало неизвестно), `none` — кончилась до начала,
 * `partial` — история началась внутри корзины.
 */
export const marketingBucketLeadHistory = (
  bucket: Pick<IMarketingRange, 'from' | 'to'>,
  leadHistoryFrom: MarketingDate | null | undefined,
): 'full' | 'partial' | 'none' => {
  if (!leadHistoryFrom || bucket.from >= leadHistoryFrom) return 'full';
  return bucket.to < leadHistoryFrom ? 'none' : 'partial';
};

/**
 * Сравнимы ли лидовые показатели с окном сравнения (F-E15): окно сравнения
 * начинается раньше истории CRM-лидов — нет («нет данных для сравнения»
 * вместо «было 0»). Начало истории неизвестно (поле не пришло) — верим
 * флагу сервера «сравнение неполное». Окна сравнения нет — сравнимы (дельт
 * и так нет).
 */
export const marketingLeadsComparable = (
  compareRange: Pick<IMarketingRange, 'from'> | null | undefined,
  leadHistoryFrom: MarketingDate | null | undefined,
  comparePartial = false,
): boolean => {
  if (!compareRange) return true;
  if (leadHistoryFrom === undefined) return !comparePartial;
  if (leadHistoryFrom === null) return true;
  return compareRange.from >= leadHistoryFrom;
};

export interface IMarketingPeriodMatrixOptions {
  /** Начало истории CRM-лидов (сервер); `null` — неизвестно / не важно. */
  leadHistoryFrom?: MarketingDate | null;
}

/**
 * Ячейки таблицы периодов: строка — показатель, колонка — корзина окна
 * (новые справа). Дельта — к предыдущей колонке; частичные значения
 * (расход за собранные дни) не сравниваются. Неполная корзина (текущая
 * неделя / месяц или первая колонка окна, начатого не с понедельника /
 * 1-го) с полной тоже не сравнивается: 3 дня против 7 — не «−60 %», а
 * разная длина (повторное ревью 0103, A2).
 */
export const marketingPeriodMatrix = (
  aligned: ReadonlyArray<IMarketingAlignedPoint<IMarketingSeriesPoint>>,
  rows: ReadonlyArray<MarketingPeriodMatrixRow> = MARKETING_PERIOD_MATRIX_ROWS,
  { leadHistoryFrom = null }: IMarketingPeriodMatrixOptions = {},
): Record<MarketingPeriodMatrixRow, IMarketingPeriodCell[]> =>
  rows.reduce((acc, row) => {
    // F-E15: у лидовых строк корзины до начала истории CRM — «нет данных»
    // (`null`), а не 0; корзина, внутри которой история началась, — со
    // значением, но не сравнивается (часть дней без лидов вообще).
    const leadBased = marketingIsLeadBasedMetric(row);
    const history = (index: number) =>
      leadBased
        ? marketingBucketLeadHistory(aligned[index].bucket, leadHistoryFrom)
        : 'full';
    const valueAt = (index: number) =>
      history(index) === 'none'
        ? { value: null, partial: false }
        : rowValue(aligned[index].current?.metrics ?? null, row);
    const cells = aligned.map((point, index) => {
      const { value, partial } = valueAt(index);
      const before =
        index > 0 ? valueAt(index - 1) : { value: null, partial: false };
      const bucketsPartial =
        point.partial ||
        (index > 0 && aligned[index - 1].partial) ||
        history(index) !== 'full' ||
        (index > 0 && history(index - 1) !== 'full');
      const comparable =
        value !== null &&
        before.value !== null &&
        !partial &&
        !before.partial &&
        !bucketsPartial;
      const diff = comparable
        ? (value as number) - (before.value as number)
        : null;
      const share =
        row !== 'crmLeadsRepeat' &&
        marketingIsShareFormat(MARKETING_METRICS[row].format);
      const deltaPct =
        comparable && before.value !== 0 && !share
          ? Math.round(
              ((diff as number) * 1000) / Math.abs(before.value as number),
            ) / 10
          : null;
      const deltaPoints =
        comparable && share
          ? marketingDeltaPoints(value as number, before.value as number)
          : null;
      const previous = rowValue(point.previous?.metrics ?? null, row).value;
      return {
        value,
        partial,
        deltaPct,
        deltaPoints,
        diff,
        confidence: comparable
          ? confidenceOf(row, value, before.value)
          : ('none' as const),
        previous,
      };
    });
    return { ...acc, [row]: cells };
  }, {} as Record<MarketingPeriodMatrixRow, IMarketingPeriodCell[]>);

/**
 * Повторное ревью 0103 (A13, B7): с какого дня в окне есть история
 * CRM-лидов — начало первой корзины с лидами, если до неё в окне были
 * корзины без единого лида (история начинается внутри окна). `null` —
 * лиды есть с первой корзины (история не короче окна) или их нет вовсе.
 * Оценка по ряду: точную дату начала истории сервер пока не отдаёт.
 */
export const marketingLeadHistoryStart = (
  points: ReadonlyArray<{ from: MarketingDate; leads: number | null }>,
): MarketingDate | null => {
  const first = points.findIndex((point) => (point.leads ?? 0) > 0);
  return first > 0 ? points[first].from : null;
};

/**
 * Пакет 2: начало истории CRM-лидов ВНУТРИ окна — поле сервера
 * (`marketingOverview.leadHistoryFrom`, от фильтров не зависит), если оно
 * пришло: день раньше начала окна — история не короче окна (`null`); лидов
 * нет вовсе (`null` сервера) — тоже `null`. Поля нет (`undefined`: старый
 * ответ или обзор ещё едет) — запасная оценка по ряду (`estimate`).
 */
export const marketingLeadHistoryFrom = (
  server: MarketingDate | null | undefined,
  range: IMarketingRange,
  estimate: () => MarketingDate | null,
): MarketingDate | null => {
  if (server === undefined) return estimate();
  if (server === null) return null;
  return server > range.from ? server : null;
};

/**
 * Фильтр сужает лиды (канал, курс, предложение, кампания, объявление,
 * юрлицо): тогда «первые лиды в окне» — первые лиды СРЕЗА, а не начало
 * истории CRM, и о начале истории по ряду судить нельзя.
 */
export const marketingFilterNarrowsLeads = (
  filter: Partial<IMarketingFilter>,
): boolean =>
  Boolean(
    filter.channels?.length ||
      filter.familySlugs?.length ||
      filter.offers?.length ||
      filter.campaignIds?.length ||
      filter.adsetIds?.length ||
      filter.adIds?.length ||
      filter.legalEntityId,
  );

/** Значение показателя точки ряда — для спарклайна плитки. */
export const marketingSeriesValues = (
  points: ReadonlyArray<IMarketingSeriesPoint | null>,
  key: MarketingMetricKey,
): Array<number | null> =>
  points.map((point) =>
    point
      ? marketingMetricValue(marketingDerivedMetrics(point.metrics), key)
      : null,
  );

/* ── Разбивка главного графика (этап 2: `marketingLeadDynamics`) ─────── */

/** `split` адреса → `groupBy` динамики лидов. */
export const MARKETING_SPLIT_GROUP_BY: Readonly<
  Record<MarketingSplit, string>
> = {
  channel: 'channel',
  family: 'family',
  offer: 'offer',
  path: 'path_kind',
};

/**
 * `split` адреса → `splitBy` когорт; у когорты нет оффера (`null` —
 * разбивки нет).
 */
export const MARKETING_COHORT_SPLIT_BY: Readonly<
  Record<MarketingSplit, string | null>
> = {
  channel: 'channel',
  family: 'family',
  offer: null,
  path: 'path_kind',
};

/** Метрики главного графика, у которых есть разбивка (лиды и продажи). */
export const MARKETING_SPLIT_METRICS: ReadonlyArray<MarketingLeadMetric> = [
  'crmLeads',
  'sales',
];

/** Групп легенды разбивки (остальное — «прочее»). */
export const MARKETING_SPLIT_TOP = 8;

/**
 * Ключ «прочее» сервера (группы за пределами top у динамики лидов и
 * многоканальных когорт). Служебный `__rest__`, а не `other`: тип страницы
 * или slug курса `other` — обычная группа со своей подписью (ревью 0103 L4).
 */
export const MARKETING_OTHER_KEY = '__rest__';

/** Вклады в корзину, которые называет подсказка. */
export const MARKETING_TOP_CONTRIBUTIONS = 3;

/** Цвет группы: канал — свой цвет канала, курс — свой цвет курса (по slug,
 * стабилен между графиками), прочее — палитра по месту. */
export const marketingSplitColor = (
  split: MarketingSplit,
  key: string,
  index: number,
): string => {
  if (key === MARKETING_OTHER_KEY || key === MARKETING_NONE_KEY) {
    return MARKETING_SPLIT_REST_COLOR;
  }
  if (split === 'channel') {
    const code = marketingChannelFromKey(key);
    if (isMarketingChannel(code)) return marketingChannelColor(code);
  }
  if (split === 'family') return marketingFamilyColor(key);
  return MARKETING_SPLIT_PALETTE[index % MARKETING_SPLIT_PALETTE.length];
};

/** Значение метрики разбивки у группы или точки. */
const splitValue = (
  item: Pick<IMarketingLeadGroupValue, 'crmLeads' | 'sales'>,
  metric: MarketingLeadMetric,
): number => (metric === 'sales' ? item.sales : item.crmLeads);

/** Итог корзины сравнения по метрике; `null` — у корзины нет пары. */
const previousValue = (
  point: IMarketingLeadDynamicsPoint,
  metric: MarketingLeadMetric,
): number | null => {
  const raw = metric === 'sales' ? point.previousSales : point.previousCrmLeads;
  return raw === null || raw === undefined ? null : raw;
};

export interface IMarketingSplitBucket {
  point: IMarketingLeadDynamicsPoint;
  /** Значения слоёв — по `groups` легенды. */
  stack: number[];
  current: number;
  previous: number | null;
}

/**
 * Динамика лидов → слои стека по легенде сервера (её порядок: первые по
 * CRM-лидам, «прочее» — последним). Значение группы, которой нет в точке, —
 * 0: сервер отдаёт все группы легенды у каждой точки.
 */
export const marketingSplitBuckets = (
  dynamics: IMarketingLeadDynamics,
  metric: MarketingLeadMetric,
): IMarketingSplitBucket[] =>
  dynamics.points.map((point) => {
    const byKey = new Map(point.groups.map((group) => [group.key, group]));
    const stack = dynamics.groups.map((group) => {
      const value = byKey.get(group.key);
      return value ? splitValue(value, metric) : 0;
    });
    return {
      point,
      stack,
      current: splitValue(point, metric),
      previous: previousValue(point, metric),
    };
  });

export interface IMarketingContribution {
  key: string;
  value: number;
  /** Доля в корзине, 0..1; корзина пустая — `null`. */
  share: number | null;
}

/** Топ вкладов в корзину (подсказка «кто дал корзину»). */
export const marketingTopContributions = (
  point: IMarketingLeadDynamicsPoint,
  metric: MarketingLeadMetric,
  limit = MARKETING_TOP_CONTRIBUTIONS,
): IMarketingContribution[] => {
  const total = splitValue(point, metric);
  return point.groups
    .map((group) => ({ key: group.key, value: splitValue(group, metric) }))
    .filter((item) => item.value > 0)
    .sort((a, b) => b.value - a.value || a.key.localeCompare(b.key))
    .slice(0, limit)
    .map((item) => ({
      ...item,
      share: total > 0 ? item.value / total : null,
    }));
};

/* ── «Откуда лиды» со сравнением сервера ─────────────────────────────── */

/**
 * Строки разреза по каналам со сравнением (`marketingBreakdown(CHANNEL,
 * compare, includeVanished)`) → доли «Откуда лиды»: текущее — `metrics`
 * строки, прошлое — её `previous` (канала не было — 0).
 */
export const marketingChannelSharesOf = (
  rows: ReadonlyArray<IMarketingBreakdownRow>,
): {
  current: IMarketingChannelShare[];
  previous: IMarketingChannelShare[];
} => {
  const current: IMarketingChannelShare[] = [];
  const previous: IMarketingChannelShare[] = [];
  rows.forEach((row) => {
    const channel = marketingChannelFromKey(row.key);
    if (!isMarketingChannel(channel)) return;
    current.push({ channel, metrics: row.metrics });
    if (row.previous) previous.push({ channel, metrics: row.previous });
  });
  return { current, previous };
};

/* ── Изменение строки таблицы к окну сравнения ───────────────────────── */

export interface IMarketingRowDelta {
  value: number | null;
  previous: number | null;
  diff: number | null;
  /** Изменение в процентах; было 0 или чего-то нет — `null`. */
  pct: number | null;
  /**
   * Повторное ревью 0103 (A12): у долей (конверсия, CTR) — разница в п. п.;
   * у прочих и без сравнения — `null`.
   */
  points: number | null;
  confidence: MarketingDeltaConfidence;
}

/**
 * Изменение показателя строки к окну сравнения (колонка «Δ» и строка под
 * цифрой). Частичный расход и отношения за собранные дни не сравниваются
 * (`null`), как у плиток.
 */
export const marketingRowDelta = (
  metrics: IMarketingMetrics | null | undefined,
  previous: IMarketingMetrics | null | undefined,
  key: MarketingMetricKey,
): IMarketingRowDelta => {
  const now = metrics
    ? marketingMetricView(marketingDerivedMetrics(metrics), key)
    : null;
  const before = previous
    ? marketingMetricView(marketingDerivedMetrics(previous), key)
    : null;
  const value = now && !now.partial ? now.value : null;
  const was = before && !before.partial ? before.value : null;
  const diff = value !== null && was !== null ? value - was : null;
  const pct =
    diff !== null && was !== null && was !== 0
      ? Math.round((diff * 1000) / Math.abs(was)) / 10
      : null;
  const { format } = MARKETING_METRICS[key];
  const points =
    value !== null && was !== null && marketingIsShareFormat(format)
      ? marketingDeltaPoints(value, was, format)
      : null;
  return {
    value: now ? now.value : null,
    previous: before ? before.value : null,
    diff,
    pct,
    points,
    confidence: marketingMetricConfidence(key, value, was),
  };
};

/**
 * Повторное ревью 0103 (B10): на экране есть хоть одна серая дельта «мало
 * данных» — тогда и только тогда под ней легенда серого цвета. Пары —
 * строки таблицы (текущие и окно сравнения), ключи — показанные столбцы.
 */
export const marketingAnySmallBase = (
  pairs: ReadonlyArray<{
    metrics: IMarketingMetrics | null | undefined;
    previous?: IMarketingMetrics | null;
  }>,
  keys: ReadonlyArray<MarketingMetricKey>,
): boolean =>
  pairs.some(
    ({ metrics, previous }) =>
      Boolean(metrics && previous) &&
      keys.some((key) => {
        const delta = marketingRowDelta(metrics, previous, key);
        return delta.diff !== null && delta.confidence === 'smallBase';
      }),
  );

/** Число из Decimal-строки или числа (выручка динамики лидов). */
export const marketingDecimalValue = (
  value: string | number | null | undefined,
): number | null =>
  value === null || value === undefined ? null : marketingNumber(value);

/* ── Договоры (`marketingContracts`) — коды корзин и видов ───────────── */

/** Время от отправки до подписи (`timeToSign`). */
export const MARKETING_CONTRACT_TIME_TO_SIGN_KEYS = [
  'lt1d',
  '1_3d',
  '3_7d',
  '7_14d',
  '14d_plus',
] as const;

/** Сколько дней ждут подписи (`pendingAge`). */
export const MARKETING_CONTRACT_PENDING_AGE_KEYS = [
  '0_3d',
  '4_7d',
  '8_14d',
  '15_30d',
  '31d_plus',
] as const;

/** Виды договоров (`byKind`): прямые, Dofinansowanie, B2B. */
export const MARKETING_CONTRACT_KINDS = [
  'direct',
  'funding',
  'company',
] as const;

/**
 * Договоры «юрлицо неизвестно» (ревью 0103 L6): при фильтре юрлица
 * неподписанный PandaDoc без выведенного юрлица в итоги и долю подписания
 * не входит — сервер отдаёт его отдельной строкой. Показываем строку, только
 * если в ней что-то есть; иначе (нет фильтра или пусто) — `null`.
 */
export const marketingUnknownLegalEntity = (
  counts: IMarketingContractsCounts | null | undefined,
): IMarketingContractsCounts | null =>
  counts && counts.sent + counts.signed + counts.pending + counts.lost > 0
    ? counts
    : null;

/* ── «Неделя к неделе»: шаг по умолчанию и активный пресет (пакет 2) ── */

/**
 * Окно длиннее этого числа дней на «Неделе к неделе» по дням не
 * раскладывается: 30 дневных колонок не читаются — по умолчанию недели.
 */
export const MARKETING_PERIODS_DAY_LIMIT = 14;

/**
 * Шаг «Недели к неделе» (пакет 2, P3): авто-шаг (`step` не задан) на окне
 * длиннее двух недель — недели, а не дни; заданный руками — как задан.
 */
export const marketingPeriodsStep = (
  step: MarketingStep | null,
  range: IMarketingRange,
): MarketingStep | null =>
  step === null && marketingRangeDays(range) > MARKETING_PERIODS_DAY_LIMIT
    ? 'week'
    : step;

/**
 * Пресет «периодов», который сейчас на экране: то же окно и тот же шаг —
 * кнопка подсвечена; ни один не совпал — `null`.
 */
export const marketingActivePeriodPreset = (
  range: IMarketingRange,
  granularity: MarketingGranularityCode,
  today: MarketingDate,
): MarketingPeriodMatrixPreset | null =>
  MARKETING_PERIOD_MATRIX_PRESETS.find((preset) => {
    const next = marketingPeriodMatrixPreset(preset, today);
    return (
      next.range.from === range.from &&
      next.range.to === range.to &&
      MARKETING_STEP_GRANULARITY[next.step] === granularity
    );
  }) ?? null;
